"use client";
import React from 'react';
import { TelegramIcon, InfoCircleIcon, ClapperboardIcon, TriangleExclamationIcon, EnvelopeIcon } from '@/components/ui/Icons';
import { TELEGRAM_URL } from '@/config';
import { useRedirect } from '@/context/RedirectContext';

export default function ContactClient() {
    const { redirect } = useRedirect();

    const handleSupportClick = (category: string) => {
        redirect(category, TELEGRAM_URL);
    };

    return (
        <div className="space-y-6 sm:space-y-8">
            {/* Primary Telegram Hub Card */}
            <div className="relative group overflow-hidden rounded-2xl sm:rounded-[2.25rem] contact-hero-card p-5 sm:p-10 text-center transition-all duration-300">
                {/* Glowing Background Accents */}
                <div className="absolute -top-24 -left-24 w-48 h-48 rounded-full bg-cyan-500/10 dark:bg-cyan-500/5 blur-[80px] pointer-events-none group-hover:bg-cyan-500/20 dark:group-hover:bg-cyan-500/10 transition-all duration-500" />
                <div className="absolute -bottom-24 -right-24 w-48 h-48 rounded-full bg-blue-500/10 dark:bg-blue-500/5 blur-[80px] pointer-events-none group-hover:bg-blue-500/20 dark:group-hover:bg-blue-500/10 transition-all duration-500" />

                <div className="relative z-10 space-y-4 sm:space-y-6">
                    <div className="mx-auto h-14 w-14 sm:h-20 sm:w-20 rounded-[1.25rem] sm:rounded-[1.75rem] bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20 shadow-inner group-hover:scale-105 transition-transform duration-300">
                        <TelegramIcon className="text-2xl sm:text-4xl text-cyan-600 dark:text-cyan-400" />
                    </div>
                    
                    <div className="space-y-2">
                        <h3 className="text-base sm:text-2xl font-black text-[var(--main-text)] tracking-tight">Direct Support & Community Hub</h3>
                        <p className="text-[11px] sm:text-base text-[var(--secondary-text)] max-w-xl mx-auto leading-relaxed">
                            Join our active community of 10K+ members. Get immediate support, request titles, report dead links, and download high-quality mirrors.
                        </p>
                    </div>

                    <div className="pt-1 sm:pt-2">
                        <button
                            type="button"
                            onClick={() => handleSupportClick("Telegram Community Support")}
                            className="telegram-button px-5 py-3 sm:px-10 sm:py-4 rounded-full text-xs sm:text-lg font-bold text-white transition-all duration-300 shadow-lg hover:-translate-y-0.5 sm:hover:-translate-y-1 hover:shadow-2xl hover:shadow-cyan-500/50 bg-gradient-to-r from-cyan-500 to-blue-500 cursor-pointer inline-flex items-center gap-2.5 sm:gap-3 w-full sm:w-auto justify-center"
                        >
                            <TelegramIcon className="text-base sm:text-2xl" />
                            <span>Launch Telegram Support</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Service Capabilities Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
                {/* Card A: Request Movies */}
                <div
                    className="group text-left p-4 sm:p-6 rounded-xl sm:rounded-2xl contact-category-card flex flex-col justify-between min-h-[120px] sm:min-h-[150px]"
                >
                    <div className="space-y-2.5 sm:space-y-3">
                        <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg sm:rounded-xl bg-red-500/10 flex items-center justify-center border border-red-500/20 group-hover:scale-105 transition-transform duration-300">
                            <ClapperboardIcon className="text-base sm:text-xl text-red-700 dark:text-red-400" />
                        </div>
                        <h5 className="font-bold text-[var(--main-text)] text-xs sm:text-base group-hover:text-red-700 dark:group-hover:text-red-400 transition-colors">Request Content</h5>
                        <p className="text-[11px] sm:text-xs text-[var(--secondary-text)] leading-relaxed">
                            Can't find a specific movie, TV series, or complete season pack? Submit requests directly to our uploaders.
                        </p>
                    </div>
                </div>

                {/* Card B: Link Troubleshooting */}
                <div
                    className="group text-left p-4 sm:p-6 rounded-xl sm:rounded-2xl contact-category-card flex flex-col justify-between min-h-[120px] sm:min-h-[150px]"
                >
                    <div className="space-y-2.5 sm:space-y-3">
                        <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg sm:rounded-xl bg-red-500/10 flex items-center justify-center border border-red-500/20 group-hover:scale-105 transition-transform duration-300">
                            <TriangleExclamationIcon className="text-base sm:text-xl text-red-700 dark:text-red-400" />
                        </div>
                        <h5 className="font-bold text-[var(--main-text)] text-xs sm:text-base group-hover:text-red-700 dark:group-hover:text-red-400 transition-colors">Report Dead Mirrors</h5>
                        <p className="text-[11px] sm:text-xs text-[var(--secondary-text)] leading-relaxed">
                            Encountered a quota limit, dead Google Drive mirror, or link error? We restore mirrors within 24 hours.
                        </p>
                    </div>
                </div>

                {/* Card C: Partnerships & Feedback */}
                <div
                    className="group text-left p-4 sm:p-6 rounded-xl sm:rounded-2xl contact-category-card flex flex-col justify-between min-h-[120px] sm:min-h-[150px]"
                >
                    <div className="space-y-2.5 sm:space-y-3">
                        <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg sm:rounded-xl bg-red-500/10 flex items-center justify-center border border-red-500/20 group-hover:scale-105 transition-transform duration-300">
                            <EnvelopeIcon className="text-base sm:text-xl text-red-700 dark:text-red-400" />
                        </div>
                        <h5 className="font-bold text-[var(--main-text)] text-xs sm:text-base group-hover:text-red-700 dark:group-hover:text-red-400 transition-colors">General Inquiry</h5>
                        <p className="text-[11px] sm:text-xs text-[var(--secondary-text)] leading-relaxed">
                            For brand partnership inquiries, features suggestions, site layout bugs, or general feedback about the platform.
                        </p>
                    </div>
                </div>
            </div>

            {/* Why Telegram Info Box */}
            <div className="text-xs sm:text-sm p-4 sm:p-6 rounded-xl sm:rounded-2xl contact-info-box text-left leading-relaxed">
                <h5 className="font-bold text-[var(--main-text)] mb-2 text-xs sm:text-base flex items-center gap-2">
                    <InfoCircleIcon className="text-red-700 dark:text-red-400 flex-shrink-0" /> Why Telegram?
                </h5>
                <p className="text-[11px] sm:text-xs text-[var(--secondary-text)]">
                    Telegram is the fastest way to get updates, report issues, and connect directly with our team. By managing requests on Telegram, our uploaders can restore broken mirrors in real-time, keeping the library unthrottled and always online without email delay bottlenecks.
                </p>
            </div>
        </div>
    );
}
